import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoTranslationsResult,
  PostVideoTranslationsOptions,
  DeleteVideoTranslationsResult,
  DeleteVideoTranslationsOptions,
  GetVideoTranslationsOptions,
  GetVideoTranslationsResult,
  getVideoTranslations,
  PostVideoTranslationsFields,
  postVideoTranslations,
  DeleteVideoTranslationsFields,
  deleteVideoTranslations,
  GetVideoTranslationsFields,
} from '../fetchers/video/translations';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoTranslationsOptions<Fields extends GetVideoTranslationsFields> =
  GetVideoTranslationsOptions<Fields>;

/**
 * Get a list of video translation jobs for the user
 * This method returns a list of the specified video's translation jobs for the authenticated user.
 */

export function useGetVideoTranslations<Fields extends GetVideoTranslationsFields>(
  getOptions:
    | UseGetVideoTranslationsOptions<Fields>
    | (() => UseGetVideoTranslationsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoTranslationsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/translations${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoTranslations({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoTranslations, {
    endpoint: '/videos/:videoId/translations',
    method: 'GET',
  });
}

/**
 * Get a list of video translation jobs for the user
 * This method returns a list of the specified video's translation jobs for the authenticated user.
 */

export function useGetVideoTranslationsLazy<Fields extends GetVideoTranslationsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoTranslationsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoTranslationsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/translations${serializeQuery(options)}`,
          getVideoTranslations({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoTranslationsLazy, {
    endpoint: '/videos/:videoId/translations',
    method: 'GET',
  });
}

type UseGetVideoTranslationsOptionsInfinite<Fields extends GetVideoTranslationsFields> =
  GetVideoTranslationsOptions<Fields>;

/**
 * Get a list of video translation jobs for the user
 * This method returns a list of the specified video's translation jobs for the authenticated user.
 */

export function useGetVideoTranslationsInfinite<Fields extends GetVideoTranslationsFields>(
  getOptions:
    | UseGetVideoTranslationsOptionsInfinite<Fields>
    | (() => UseGetVideoTranslationsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoTranslationsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (pageIndex: number, previousPageData: GetVideoTranslationsResult<Fields> | null) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/translations?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoTranslations({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoTranslationsInfinite, {
    endpoint: '/videos/:videoId/translations',
    method: 'GET',
  });
}

type UsePostVideoTranslationsOptions<Fields extends PostVideoTranslationsFields> =
  PostVideoTranslationsOptions<Fields>;

/**
 * Start a video translation job for the user
 * This method starts a translation job of the specified video for the authenticated user.
 */

export function usePostVideoTranslations<Fields extends PostVideoTranslationsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoTranslationsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoTranslationsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postVideoTranslations({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoTranslations, {
    endpoint: '/videos/:videoId/translations',
    method: 'POST',
  });
}

type UseDeleteVideoTranslationsOptions<Fields extends DeleteVideoTranslationsFields> =
  DeleteVideoTranslationsOptions<Fields>;

/**
 * Delete an existing translation track for a video
 * This method deletes an existing translation track for the specified video.
 */

export function useDeleteVideoTranslations<Fields extends DeleteVideoTranslationsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteVideoTranslationsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteVideoTranslationsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await deleteVideoTranslations({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideoTranslations, {
    endpoint: '/videos/:videoId/translations',
    method: 'DELETE',
  });
}
